import { CircularProgress, type SxProps, type Theme } from "@mui/material";
import { PortalTooltip } from "@gui/widgets/PortalTooltip";

/** px across, and the ring's thickness where the default (3.6) would vanish at that size. */
const SIZES = {
	/** On a count badge, in place of the count while it loads. */
	badge: { px: 10, thickness: 6 },
	/** Beside caption-sized text. */
	xs: { px: 12, thickness: undefined },
	/** Beside dense text, a card header's chips. */
	sm: { px: 14, thickness: undefined },
	/** Beside body text, in a small icon's slot (a menu item, an alert, a field adornment). */
	md: { px: 16, thickness: undefined },
	/** Beside a heading. */
	lg: { px: 20, thickness: undefined },
} as const;

interface InlineProgressProps {
	size?: keyof typeof SIZES;
	/**
	 * Names the indicator when nothing beside it says what is running (a card header, beside a
	 * picker). Left out, it is hidden from assistive tech: the text beside it carries the meaning.
	 */
	label?: string;
	/** Show `label` as a tooltip too. */
	tooltip?: boolean;
	/** `inherit` takes the surrounding text or badge colour. */
	color?: "primary" | "inherit";
	sx?: SxProps<Theme>;
}

/**
 * A small "working" ring that sits inline: beside text, in an icon slot, on a badge. It shows
 * that something is running; it is not a load (AsyncState) or a busy button (BusyButton,
 * BusyIconButton). docs/design-system.md §4.
 */
export function InlineProgress(props: InlineProgressProps) {
	const size = SIZES[props.size ?? "md"];
	const ring = (
		<CircularProgress
			size={size.px}
			thickness={size.thickness}
			color={props.color}
			aria-label={props.label}
			aria-hidden={props.label === undefined || undefined}
			sx={props.sx}
		/>
	);
	return props.tooltip && props.label ? <PortalTooltip title={props.label}>{ring}</PortalTooltip> : ring;
}
